/**
 * 浏览器直传华为 OBS（对齐旧站 packages/shared/src/uploadFile）。
 * ObsClient.putObject → 返回 CDN URL → CDN 预热。
 *
 * 对象路径：wiki4.0/{game_domain|www}/{image|video|live2d|audio|file}/{uniqueKey}{ext}
 */

import { staticUrl } from '../config/static-assets';

export type ObsUploadFileType =
  | 'image'
  | 'video'
  | 'live2d'
  | 'audio'
  | 'file';

export type ObsCredential = {
  access: string;
  secret: string;
  security_token: string;
  /** 有效秒数（自获取时刻起），非绝对时间戳 */
  expires_at: number;
  bucket_name: string;
  end_point: string;
  bucket_end_point: string;
  cdn_host: string;
  path: Record<string, string>;
};

export type UploadToObsOptions = {
  file: File | Blob;
  /** URL 域名第二级；大首页 / 平台用 www（也兼容传入 platform） */
  gameDomain: string;
  fileType?: ObsUploadFileType;
  fileName?: string;
  onProgress?: (percent: number) => void;
  /** 上传成功后提交 CDN 预热，默认 true */
  preload?: boolean;
};

type ObsClientInstance = {
  putObject: (
    params: {
      Bucket: string;
      Key: string;
      SourceFile: File | Blob;
      ProgressCallback?: (
        transferredAmount: number,
        totalAmount: number,
        totalSeconds: number,
      ) => void;
    },
    callback: (
      err: Error | null,
      result: { CommonMsg?: { Status?: number; Code?: string; Message?: string } },
    ) => void,
  ) => void;
};

type ObsClientCtor = new (options: {
  access_key_id: string;
  secret_access_key: string;
  security_token: string;
  server: string;
}) => ObsClientInstance;

declare global {
  interface Window {
    ObsClient?: ObsClientCtor;
  }
}

const OBS_SDK_SRC = '/vendor/esdk-obs-browserjs-3.19.5.min.js';
const REFRESH_BUFFER_MS = 5 * 60 * 1000;
const CACHE_KEY = 'wiki_v2_obs_client_params';

/** 生成唯一 key（与旧 wiki createUniqueKey 一致） */
export function createUniqueKey(length = 8) {
  return (
    Math.random().toString().substring(2).substring(0, length) +
    Date.now().toString(36)
  );
}

type CachedCredential = {
  value: ObsCredential;
  expireAtMs: number;
};

let cachedCredential: CachedCredential | null = null;
let credentialInflight: Promise<ObsCredential> | null = null;
let obsSdkPromise: Promise<ObsClientCtor> | null = null;

function normalizeGameDomain(gameDomain: string) {
  const value = gameDomain.trim().toLowerCase();
  if (!value || value === 'platform' || value === 'portal') return 'www';
  return value;
}

function loadObsSdk(): Promise<ObsClientCtor> {
  if (typeof window !== 'undefined' && typeof window.ObsClient === 'function') {
    return Promise.resolve(window.ObsClient);
  }
  if (obsSdkPromise) return obsSdkPromise;

  obsSdkPromise = new Promise((resolve, reject) => {
    const fail = (message: string) => {
      obsSdkPromise = null;
      document.querySelectorAll('script[data-obs-sdk="1"]').forEach((node) => {
        node.remove();
      });
      reject(new Error(message));
    };
    const succeed = () => {
      if (typeof window.ObsClient === 'function') resolve(window.ObsClient);
      else fail('ObsClient 未挂载到 window（脚本可能被 SPA 回退成了 HTML）');
    };

    const existing = document.querySelector<HTMLScriptElement>(
      `script[data-obs-sdk="1"]`,
    );
    if (existing) {
      if (typeof window.ObsClient === 'function') {
        resolve(window.ObsClient);
        return;
      }
      existing.remove();
    }
    const script = document.createElement('script');
    script.src = staticUrl(OBS_SDK_SRC);
    script.async = true;
    script.dataset.obsSdk = '1';
    script.onload = succeed;
    script.onerror = () => fail('ObsClient 脚本加载失败');
    document.head.appendChild(script);
  });

  return obsSdkPromise;
}

function detectFileType(
  file: File | Blob,
  fileName: string,
  explicit?: ObsUploadFileType,
): ObsUploadFileType {
  if (explicit) return explicit;
  const mime = (file.type || '').toLowerCase();
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('video/')) return 'video';
  if (mime.startsWith('audio/')) return 'audio';
  const ext = fileName.split('.').pop()?.toLowerCase() || '';
  if (['skel', 'atlas'].includes(ext)) return 'live2d';
  return 'file';
}

function resolveExtension(file: File | Blob, fileName: string) {
  const match = fileName.match(/\.([0-9a-z]+)(?:[?#]|$)/i);
  if (match?.[0]) return match[0].toLowerCase();
  const mime = (file.type || '').toLowerCase();
  const mimeMap: Record<string, string> = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'image/gif': '.gif',
    'image/avif': '.avif',
    'video/mp4': '.mp4',
    'video/webm': '.webm',
    'video/quicktime': '.mov',
    'audio/mpeg': '.mp3',
    'audio/mp4': '.m4a',
    'audio/wav': '.wav',
    'application/json': '.json',
  };
  return mimeMap[mime] || '';
}

function buildObjectKey(
  gameDomain: string,
  fileType: ObsUploadFileType,
  file: File | Blob,
  fileName: string,
) {
  const domain = normalizeGameDomain(gameDomain);
  const ext = resolveExtension(file, fileName);
  return `wiki4.0/${domain}/${fileType}/${createUniqueKey()}${ext}`;
}

/** cdn_host + objectKey → https CDN URL（勿存 OBS 域名） */
export function buildCdnUrl(cdnHost: string, objectKey: string) {
  const key = objectKey.replace(/^\/+/, '');
  let host = cdnHost.trim();
  if (!host) throw new Error('CDN 域名缺失');
  if (host.startsWith('//')) host = `https:${host}`;
  if (!/^https?:\/\//i.test(host)) host = `https://${host}`;
  if (!host.endsWith('/')) host += '/';
  return `${host}${key}`;
}

function readSessionCache(): CachedCredential | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedCredential;
    if (!parsed?.value?.access || !parsed.expireAtMs) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSessionCache(cache: CachedCredential) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* quota / private mode */
  }
}

async function fetchObsCredential(gameDomain: string): Promise<ObsCredential> {
  const game = normalizeGameDomain(gameDomain);
  const response = await fetch(
    `/api/games/${encodeURIComponent(game)}/obs/credential`,
    { credentials: 'same-origin' },
  );
  const text = await response.text();
  let payload: Partial<ObsCredential> & { error?: string } = {};
  try {
    payload = text ? JSON.parse(text) : {};
  } catch {
    /* non-json */
  }
  if (
    !response.ok ||
    !payload.access ||
    !payload.secret ||
    !payload.security_token ||
    !payload.bucket_name ||
    !payload.end_point ||
    !payload.cdn_host
  ) {
    throw new Error(
      `${payload.error || '获取 OBS 临时凭证失败'}（HTTP ${response.status || '网络错误'}）`,
    );
  }
  return {
    access: payload.access,
    secret: payload.secret,
    security_token: payload.security_token,
    expires_at: Number(payload.expires_at) || 3600,
    bucket_name: payload.bucket_name,
    end_point: payload.end_point,
    bucket_end_point: payload.bucket_end_point || '',
    cdn_host: payload.cdn_host,
    path: payload.path && typeof payload.path === 'object' ? payload.path : {},
  };
}

/** 全局缓存临时凭证（内存 + sessionStorage）；过期前复用。 */
export async function getObsCredential(
  gameDomain = 'www',
): Promise<ObsCredential> {
  const now = Date.now();
  if (!cachedCredential) {
    const fromSession = readSessionCache();
    if (fromSession && fromSession.expireAtMs - REFRESH_BUFFER_MS > now) {
      cachedCredential = fromSession;
    }
  }
  if (
    cachedCredential &&
    cachedCredential.expireAtMs - REFRESH_BUFFER_MS > now
  ) {
    return cachedCredential.value;
  }
  if (credentialInflight) return credentialInflight;

  credentialInflight = fetchObsCredential(gameDomain)
    .then((value) => {
      const next = {
        value,
        expireAtMs: Date.now() + Math.max(60, value.expires_at) * 1000,
      };
      cachedCredential = next;
      writeSessionCache(next);
      credentialInflight = null;
      return value;
    })
    .catch((error) => {
      credentialInflight = null;
      throw error;
    });

  return credentialInflight;
}

export async function preloadCdnUrls(urls: string[]) {
  const list = [...new Set(urls.map((url) => url.trim()).filter(Boolean))];
  if (!list.length) return;
  try {
    await fetch('/api/cdn/preload', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ url: list.slice(0, 50) }),
    });
  } catch {
    /* 预热失败不阻断上传结果 */
  }
}

function normalizeObsServer(endPoint: string) {
  return endPoint.trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
}

function putObjectWithObsClient(
  ObsClient: ObsClientCtor,
  credential: ObsCredential,
  objectKey: string,
  file: File | Blob,
  onProgress?: (percent: number) => void,
) {
  const client = new ObsClient({
    access_key_id: credential.access,
    secret_access_key: credential.secret,
    security_token: credential.security_token,
    // 与旧站一致：使用 end_point 作为 server
    server: normalizeObsServer(credential.end_point),
  });
  const bucketName = credential.bucket_name || 'wiki-obs';

  return new Promise<void>((resolve, reject) => {
    client.putObject(
      {
        Bucket: bucketName,
        Key: objectKey,
        SourceFile: file,
        ProgressCallback: (transferredAmount, totalAmount) => {
          if (!onProgress || !totalAmount) return;
          onProgress(Math.round((transferredAmount * 100) / totalAmount));
        },
      },
      (err, result) => {
        const status = result?.CommonMsg?.Status ?? 0;
        if (!err && status > 0 && status < 300) {
          resolve();
          return;
        }
        const message =
          err?.message ||
          result?.CommonMsg?.Message ||
          result?.CommonMsg?.Code ||
          '未知错误';
        reject(new Error(`上传失败: ${message}`));
      },
    );
  });
}

/**
 * 统一上传入口（ObsClient）：返回可持久化的 CDN URL。
 */
export async function uploadToObs(options: UploadToObsOptions): Promise<string> {
  const fileName =
    options.fileName ||
    (options.file instanceof File && options.file.name) ||
    'upload.bin';
  const fileType = detectFileType(options.file, fileName, options.fileType);
  // 先拉凭证再加载 SDK，避免 SDK 被 SPA HTML 顶掉时看起来像没打 credential
  const credential = await getObsCredential(options.gameDomain);
  const ObsClient = await loadObsSdk();
  const objectKey = buildObjectKey(
    options.gameDomain,
    fileType,
    options.file,
    fileName,
  );

  await putObjectWithObsClient(
    ObsClient,
    credential,
    objectKey,
    options.file,
    options.onProgress,
  );

  const cdnUrl = buildCdnUrl(credential.cdn_host, objectKey);
  if (options.preload !== false) {
    // 与旧站一致：预热 https CDN 地址
    void preloadCdnUrls([cdnUrl]);
  }
  return cdnUrl;
}
